import React, { useState } from 'react';
import { Card, Button, Collapse, Badge, ListGroup } from 'react-bootstrap';
import { FaFileAlt, FaFileInvoiceDollar, FaExchangeAlt, FaReceipt, FaFolder, FaChevronDown, FaChevronRight, FaDownload, FaEnvelope, FaTrash, FaImage } from 'react-icons/fa';
import { downloadDocument } from '../../utils/documentUtils';
import { toast } from 'react-toastify';

const DocumentsGroupedView = ({ documents, receipts, token, onDelete, clientEmail }) => {
  const [openSections, setOpenSections] = useState({
    proposals: true,
    invoices: true,
    changeOrders: true,
    receipts: true,
    other: true
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Categorize documents
  const categorizeDocuments = () => {
    const categories = {
      proposals: [],
      invoices: [],
      changeOrders: [],
      other: []
    };

    documents.forEach(doc => {
      const name = doc.DocumentName?.toLowerCase() || '';
      const type = doc.DocumentType?.toLowerCase() || '';
      
      if (name.includes('proposal') || type.includes('proposal')) {
        categories.proposals.push(doc);
      } else if (name.includes('invoice') || type.includes('invoice')) {
        categories.invoices.push(doc);
      } else if (name.includes('change') || name.includes('order') || type.includes('change')) {
        categories.changeOrders.push(doc);
      } else {
        categories.other.push(doc);
      }
    });

    return categories;
  };

  const categories = categorizeDocuments();

  const getDocTypeIcon = (docName) => {
    const name = docName?.toLowerCase() || '';
    if (name.includes('proposal')) return <FaFileAlt className="text-primary" />;
    if (name.includes('invoice')) return <FaFileInvoiceDollar className="text-success" />;
    if (name.includes('change')) return <FaExchangeAlt className="text-warning" />;
    return <FaFolder className="text-secondary" />;
  };

  const renderDocumentItem = (doc) => (
    <ListGroup.Item key={doc.DocumentId} className="d-flex justify-content-between align-items-center">
      <div className="d-flex align-items-center">
        {getDocTypeIcon(doc.DocumentName)}
        <div className="ms-3">
          <div className="fw-medium">{doc.DocumentName}</div>
          <small className="text-muted">
            {new Date(doc.CreatedAt).toLocaleDateString()}
          </small>
        </div>
      </div>
      <div>
        <Button
          variant="outline-primary"
          size="sm"
          className="me-1"
          onClick={() => downloadDocument(doc.DocumentId, doc.DocumentName, token)}
        >
          <FaDownload />
        </Button>
        <Button
          variant="outline-success"
          size="sm"
          className="me-1"
          disabled={!clientEmail}
          title={!clientEmail ? "No client email" : "Email to client"}
        >
          <FaEnvelope />
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => onDelete(doc.DocumentId)}
        >
          <FaTrash />
        </Button>
      </div>
    </ListGroup.Item>
  );

  const renderReceiptItem = (receipt) => (
    <ListGroup.Item key={receipt.ScanId} className="d-flex justify-content-between align-items-center">
      <div className="d-flex align-items-center">
        <FaReceipt className="text-info" />
        <div className="ms-3">
          <div className="fw-medium">
            {receipt.StoreName || 'Unknown Store'}
            <Badge 
              bg={receipt.Status === 'approved' ? 'success' : receipt.Status === 'pending' ? 'warning' : 'secondary'} 
              className="ms-2"
            >
              {receipt.Status}
            </Badge>
          </div>
          <small className="text-muted">
            {new Date(receipt.ScannedAt).toLocaleDateString()} • 
            ${parseFloat(receipt.TotalAmount || 0).toFixed(2)} • 
            {receipt.ItemCount || 0} items
          </small>
        </div>
      </div>
      <div>
        <Button
          variant="outline-info"
          size="sm"
          className="me-1"
          title="View Receipt Image"
        >
          <FaImage />
        </Button>
      </div>
    </ListGroup.Item>
  );

  const renderSection = (title, icon, items, sectionKey, renderItem, badgeColor = 'primary') => (
    <Card className="mb-3">
      <Card.Header 
        className="d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => toggleSection(sectionKey)}
      >
        <div className="d-flex align-items-center">
          {openSections[sectionKey] ? <FaChevronDown className="me-2" /> : <FaChevronRight className="me-2" />}
          {icon}
          <span className="ms-2 fw-medium">{title}</span>
          <Badge bg={badgeColor} className="ms-2">{items.length}</Badge>
        </div>
      </Card.Header>
      <Collapse in={openSections[sectionKey]}>
        <div>
          {items.length > 0 ? (
            <ListGroup variant="flush">
              {items.map(renderItem)}
            </ListGroup>
          ) : (
            <Card.Body className="text-center text-muted py-4">
              No {title.toLowerCase()} yet
            </Card.Body>
          )}
        </div>
      </Collapse>
    </Card>
  );

  return (
    <div>
      {renderSection('Proposals', <FaFileAlt className="text-primary" />, categories.proposals, 'proposals', renderDocumentItem, 'primary')}
      {renderSection('Invoices', <FaFileInvoiceDollar className="text-success" />, categories.invoices, 'invoices', renderDocumentItem, 'success')}
      {renderSection('Change Orders', <FaExchangeAlt className="text-warning" />, categories.changeOrders, 'changeOrders', renderDocumentItem, 'warning')}
      {renderSection('Receipts', <FaReceipt className="text-info" />, receipts, 'receipts', renderReceiptItem, 'info')}
      {renderSection('Other Documents', <FaFolder className="text-secondary" />, categories.other, 'other', renderDocumentItem, 'secondary')}
    </div>
  );
};

export default DocumentsGroupedView;
